import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Eye, EyeOff, Info, Camera, User, MapPin } from "lucide-react";
import { Combobox } from "@/components/Combobox";
import { COUNTRIES } from "@/data/countries";
import { SPAIN_CITIES } from "@/data/spain-cities";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72034c2-4f95-4f18-be74-1d29b1871fdc/artifacts/djdyqu9t_IMG_1381.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [bio, setBio] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatar(file);
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("La contraseña debe tener al menos 6 caracteres"); return; }
    if (!termsAccepted) { setError("Debes aceptar los términos y condiciones"); return; }
    setSubmitting(true);
    try {
      await register({ username, email, password, country, city, terms_accepted: termsAccepted, promo_code: promoCode || null, bio: bio.trim() || null });
      // Upload avatar if selected
      if (avatar) {
        try {
          const formData = new FormData();
          formData.append("file", avatar);
          await axios.post(`${API}/users/avatar`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
        } catch {}
      }
      navigate("/verify-email");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5EEDB] flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-6">
          <img src={LOGO_URL} alt="Refugium" className="w-14 h-14 rounded-full mx-auto mb-3" />
          <h1 className="font-heading text-3xl text-[#5B3E2E]">Crear cuenta</h1>
          <p className="text-sm text-[#5B3E2E]/60 mt-1">Únete a la comunidad Refugium</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#FDFAF4] rounded-3xl p-6 sm:p-8 border border-[#5B3E2E]/10 shadow-[0_8px_30px_rgba(91,62,46,0.06)]">
          {error && (
            <div data-testid="register-error" className="bg-[#D9534F]/10 text-[#D9534F] text-sm rounded-xl p-3 mb-4">{error}</div>
          )}

          {/* Avatar */}
          <div className="flex flex-col items-center mb-5">
            <div className="relative">
              <div className="w-18 h-18 w-[72px] h-[72px] rounded-full bg-[#5B3E2E]/10 flex items-center justify-center overflow-hidden border-2 border-dashed border-[#5B3E2E]/20">
                {avatarPreview ? <img src={avatarPreview} alt="" className="w-full h-full object-cover" /> : <User size={28} strokeWidth={1.5} className="text-[#5B3E2E]/30" />}
              </div>
              <label className="absolute bottom-0 right-0 w-6 h-6 rounded-full bg-[#E48D57] flex items-center justify-center cursor-pointer hover:bg-[#D07C46] transition-colors">
                <Camera size={12} className="text-white" />
                <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} data-testid="register-avatar" />
              </label>
            </div>
            <p className="text-[10px] text-[#5B3E2E]/40 mt-1">Foto de perfil (opcional)</p>
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="username" className="text-[#5B3E2E] text-sm">Nombre de usuario</Label>
              <Input data-testid="register-username" id="username" value={username} onChange={(e) => setUsername(e.target.value)} required className="mt-1 bg-transparent border-[#5B3E2E]/20 rounded-xl focus:ring-1 focus:ring-[#E48D57] text-[#5B3E2E]" placeholder="Tu nombre visible" />
              <div className="flex items-start gap-2 mt-1.5 p-2 bg-[#6B9070]/5 rounded-lg">
                <Info size={12} className="text-[#6B9070] mt-0.5 flex-shrink-0" />
                <p className="text-[10px] text-[#6B9070]">El nombre de usuario será visible dentro de la comunidad y puede modificarse en cualquier momento</p>
              </div>
            </div>
            <div>
              <Label htmlFor="email" className="text-[#5B3E2E] text-sm">Email</Label>
              <Input data-testid="register-email" id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 bg-transparent border-[#5B3E2E]/20 rounded-xl focus:ring-1 focus:ring-[#E48D57] text-[#5B3E2E]" placeholder="tu@email.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#5B3E2E] text-sm">Contraseña</Label>
              <div className="relative mt-1">
                <Input data-testid="register-password" id="password" type={showPw ? "text" : "password"} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-transparent border-[#5B3E2E]/20 rounded-xl focus:ring-1 focus:ring-[#E48D57] text-[#5B3E2E] pr-10" placeholder="Mínimo 6 caracteres" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5B3E2E]/40">
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Country & City */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#5B3E2E] text-sm flex items-center gap-1"><MapPin size={12} /> País</Label>
                <div className="mt-1">
                  <Combobox
                    dataTestid="register-country"
                    value={country}
                    onChange={(v) => { setCountry(v); setCity(""); }}
                    options={COUNTRIES}
                    placeholder="Selecciona país"
                    emptyText="No encontrado"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[#5B3E2E] text-sm">Ciudad</Label>
                <div className="mt-1">
                  {country === "España" ? (
                    <Combobox
                      dataTestid="register-city"
                      value={city}
                      onChange={setCity}
                      options={SPAIN_CITIES}
                      placeholder="Selecciona ciudad"
                      emptyText="No encontrada"
                    />
                  ) : (
                    <Input
                      data-testid="register-city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      disabled={!country}
                      className="bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E] text-sm disabled:opacity-50"
                      placeholder={country ? "Escribe tu ciudad" : "Elige país primero"}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Promo Code */}
            <div>
              <Label className="text-[#5B3E2E] text-sm">Código promocional (opcional)</Label>
              <Input data-testid="register-promo" value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} className="mt-1 bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E] text-sm tracking-widest" placeholder="XXXX1234" maxLength={8} />
            </div>

            {/* Bio (opcional) */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[#5B3E2E] text-sm">Sobre ti <span className="text-[#5B3E2E]/40 text-xs font-normal">(opcional)</span></Label>
                <span className="text-[10px] text-[#5B3E2E]/40">{bio.length}/200</span>
              </div>
              <Textarea
                data-testid="register-bio"
                value={bio}
                onChange={(e) => setBio(e.target.value.slice(0, 200))}
                placeholder="Preséntate brevemente a la comunidad..."
                className="mt-1 bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E] text-sm min-h-[70px] resize-none"
                maxLength={200}
              />
              <p className="text-[10px] text-[#5B3E2E]/40 mt-1">Ayuda a otros miembros a conocerte mejor. Podrás editarlo cuando quieras.</p>
            </div>

            {/* Terms */}
            <div className="flex items-start gap-3 p-3 bg-[#5B3E2E]/3 rounded-xl">
              <Checkbox
                data-testid="register-terms"
                id="terms"
                checked={termsAccepted}
                onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                className="mt-0.5 border-[#5B3E2E]/30 data-[state=checked]:bg-[#E48D57] data-[state=checked]:border-[#E48D57]"
              />
              <label htmlFor="terms" className="text-xs text-[#5B3E2E]/70 leading-relaxed cursor-pointer">
                He leído y acepto los{" "}
                <Link to="/terms" target="_blank" className="text-[#E48D57] underline hover:text-[#D07C46]">
                  Términos y Condiciones
                </Link>
                {" "}y la{" "}
                <Link to="/privacy" target="_blank" className="text-[#E48D57] underline hover:text-[#D07C46]">
                  Política de Privacidad
                </Link>
                {" "}de Refugium
              </label>
            </div>
          </div>

          <Button data-testid="register-submit" type="submit" disabled={submitting || !termsAccepted} className="w-full mt-5 bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full py-3 font-medium transition-all disabled:opacity-40">
            {submitting ? "Creando cuenta..." : "Registrarse"}
          </Button>
        </form>

        <p className="text-center text-sm text-[#5B3E2E]/60 mt-5">
          ¿Ya tienes cuenta?{" "}
          <Link to="/login" data-testid="go-to-login" className="text-[#E48D57] hover:underline font-medium">Inicia sesión</Link>
        </p>
        <p className="text-center mt-3">
          <Link to="/" className="text-sm text-[#5B3E2E]/40 hover:text-[#5B3E2E]/60">Volver al inicio</Link>
        </p>
      </div>
    </div>
  );
}
